import { h, Component, ComponentChild } from "preact";
import cx from "classnames";
import getStore, { appState$ } from "../../store";
import { map, distinctUntilChanged } from "rxjs/operators";
import { Subscription, Subject } from "rxjs";
import moment from "moment-timezone";
import Portal from "preact-portal";

import { User } from "../../../../types/user.model";
import { LoanPayment, getLoanStatusString } from "../../../../types/loan.model";

import { toDecimal } from "../../../utils/decimal";
import {
  GENERATE_SERVERSIDE_LOAN_PAYMENT_REPORT,
  GET_LOAN_PAYMENTS,
} from "../../reducers/cashbond-loan/cashbond-loan.actions";
import { ApproveLoanPaymentModal } from "./modal/approve-loan-payment.model";
import { RejectLoanPaymentModal } from "./modal/reject-loan-payment.modal";
import { Form, FieldList, Field } from "../../../../types/form.model";
import { validateForm } from "../../../../types/validator.model";
import linkform, { updateFormFieldValue } from "../../../utils/linkform";
import { AutocompleteSearch } from "../common/autocomplete-search.component";
import { showToast } from "../../../utils/toast.service";
import { DatePicker } from "../common/date-picker";
import { ROLE_AUDITOR, ROLE_MANAGEMENT } from "../../../../constants/role";
import { PAGE_SIZE } from "../remittance/dash-remittance.component";
import { BondPayment } from "../../../../types/cashbond.model";
import { GET_BANKS, UserAction } from "../../reducers/user/user.actions";
import { BankAccount } from "../../../../types/bank-account.model";

export interface DashPaymentApprovalState {
  allPayments?: LoanPayment[];
  focusedPayment?: LoanPayment;

  currentUser?: User;
  modalState?: number;

  pages?: number[];
  currentPage?: number;
  filters?: Map<string, any>;
  filterForm?: Form;
  isFilterVisible?: boolean;

  allBankAccounts: BankAccount[];
}

export class DashPaymentApproval extends Component<
  any,
  DashPaymentApprovalState
> {
  subs: Subscription[] = [];
  form$: Subject<Form>;

  resetStartDate: Function;
  resetEndDate: Function;

  private MODAL_APPROVE_PAYMENT: number = 0;
  private MODAL_REJECT_PAYMENT: number = 1;

  constructor(props) {
    super(props);

    let filters: Map<string, any> = new Map();
    filters.set("page", 1);

    this.setState({
      modalState: -1,
      filters: filters,
      currentPage: 1,
      currentUser: null,
    });

    this.createForm();
  }

  createForm() {
    let fields: FieldList = {
      startDate: new Field([]),
      endDate: new Field([]),
      loanId: new Field([]),
      loanPaymentId: new Field([]),
      receivingBankAccount: new Field([]),
      minAmount: new Field([], null, ""),
      maxAmount: new Field([], null, ""),
      agentId: new Field([]),
      status: new Field([]),
      supervisor: new Field([]),
    };

    this.form$ = new Subject<Form>();
    this.setState({
      filterForm: new Form(fields),
    });

    this.form$.subscribe((form) => {
      validateForm(form);

      let state = {};
      state["filterForm"] = form;
      this.setState(state);
    });
  }

  componentDidMount() {
    const user$ = appState$.pipe(map((appState) => appState.user));

    this.subs.push(
      user$
        .pipe(
          map((user) => user.currentUser),
          distinctUntilChanged()
        )
        .subscribe((currentUser) => {
          this.setState({
            currentUser: currentUser,
          });
        })
    );

    this.subs.push(
      user$
        .pipe(
          map((user) => user.allBankAccounts),
          distinctUntilChanged()
        )
        .subscribe((allBankAccounts) => {
          if (allBankAccounts) {
            this.setState({
              allBankAccounts: allBankAccounts,
            });
          }
        })
    );

    const cashbondLoans$ = appState$.pipe(
      map((appState) => appState.cashbondloan)
    );

    this.subs.push(
      cashbondLoans$
        .pipe(
          map((cashbondloan) => cashbondloan.loanPayments),
          distinctUntilChanged()
        )
        .subscribe((loanPayments) => {
          this.setState({
            allPayments: loanPayments,
          });
        })
    );

    this.subs.push(
      cashbondLoans$
        .pipe(map((cashbondloan) => cashbondloan.total))
        .subscribe((total) => {
          if (total) {
            let pages: number[] = [];
            let limit = Math.ceil(total / PAGE_SIZE);

            for (let i = 0; i < limit; i++) {
              pages.push(i + 1);
            }

            this.setState({
              pages: pages,
            });
          }
        })
    );

    this.initialStoreDispatches();
  }

  initialStoreDispatches() {
    const store = getStore();

    store.dispatch({
      type: GET_LOAN_PAYMENTS,
      payload: {
        filters: this.state.filters,
      },
    });

    store.dispatch({
      type: GET_BANKS,
    } as UserAction);
  }

  onPageClicked = (page: number) => {
    let filterForm = {};
    const fieldList = this.state.filterForm.fields;

    for (let k in fieldList) {
      if (fieldList[k].value) {
        filterForm[k] = fieldList[k].value;
      }
    }

    filterForm["page"] = page;

    let filters = this.state.filters;
    filters.set("page", page);

    const store = getStore();
    store.dispatch({
      type: GET_LOAN_PAYMENTS,
      payload: {
        filters: filters,
      },
    });

    this.setState({
      currentPage: page,
    });
  };

  onModalOpened = (paramState: number, row?: any) => {
    if (paramState === this.MODAL_APPROVE_PAYMENT) {
      this.setState({
        modalState: paramState,
        focusedPayment: row as LoanPayment,
      });
    } else if (paramState === this.MODAL_REJECT_PAYMENT) {
      this.setState({
        modalState: paramState,
        focusedPayment: row as LoanPayment,
      });
    } else {
    }
  };

  onModalClose = (returnParams: any) => {
    this.setState({
      modalState: -1,
    });

    this.initialStoreDispatches();
  };

  onFilterToggleClicked = () => {
    this.setState({
      isFilterVisible: !this.state.isFilterVisible,
    });
  };

  resetStartDates = (returned: Function) => {
    this.resetStartDate = returned;
  };

  resetEndDates = (returned: Function) => {
    this.resetEndDate = returned;
  };

  resetFilters = () => {
    const fieldList = this.state.filterForm.fields;

    for (let k in fieldList) {
      fieldList[k].value = null;
    }

    this.resetStartDate();
    this.resetEndDate();

    let filters: Map<string, any> = new Map();
    filters.set("page", 1);

    this.setState({
      filters: filters,
      currentPage: 1,
    });

    this.initialStoreDispatches();
  };

  startDateSelected = (date: string) => {
    updateFormFieldValue(this.form$, this.state.filterForm, "startDate", date);
  };

  endDateSelected = (date: string) => {
    updateFormFieldValue(this.form$, this.state.filterForm, "endDate", date);
  };

  applyFilters = () => {
    let filters = this.state.filters;
    const fieldList = this.state.filterForm.fields;

    console.log(fieldList);

    for (let k in fieldList) {
      if (fieldList[k].value) {
        filters.set(k, fieldList[k].value);
      }
    }

    const store = getStore();
    store.dispatch({
      type: GET_LOAN_PAYMENTS,
      payload: {
        filters: filters,
      },
    });
  };

  onSupervisorSearchChange = (supervisor: User) => {
    updateFormFieldValue(
      this.form$,
      this.state.filterForm,
      "supervisor",
      supervisor.username
    );
  };

  generateReport = () => {
    let filters: Map<string, any> = new Map<string, any>();
    const fieldList = this.state.filterForm.fields;

    for (let k in fieldList) {
      if (fieldList[k].value) {
        filters.set(k, fieldList[k].value);
      }
    }

    filters.set("pageSize", 5000);
    filters.set("page", 1);

    const store = getStore();
    store.dispatch({
      type: GENERATE_SERVERSIDE_LOAN_PAYMENT_REPORT,
      payload: {
        filters: filters,
      },
    });
  };

  getActionsBlock = (state: DashPaymentApprovalState) => {
    let role: string;

    if (state && state.currentUser) {
      role = state.currentUser.role;
    }
    if (role && (role === ROLE_MANAGEMENT || role === ROLE_AUDITOR)) {
      return (
        <div class="row">
          <div class="col-12 col-lg-2 mb-3 mb-lg-0">
            <button
              class="btn btn-secondary w-100"
              onClick={this.generateReport}
            >
              Generate Report
            </button>
          </div>
        </div>
      );
    }
  };

  getFilterBlock = (state: DashPaymentApprovalState) => {
    return (
      <div class="container-fluid px-0 mt-3">
        <div class="row justify-content-end">
          <div class="col-12 col-lg-3 offset-9 d-flex justify-content-end">
            <a
              class="dash-remittance__filter-row__filter-btn"
              onClick={this.onFilterToggleClicked}
            >
              Filter <i class="fas fa-filter"></i>
            </a>
          </div>
        </div>
        <div class="row no-gutters">
          <div class="col-12">
            {state.isFilterVisible ? (
              <div class="dash-remittance__filter-row__filters mt-2">
                {this.getFilterForm(state)}
                {this.getFilterActions(state)}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  getFilterActions(state: DashPaymentApprovalState) {
    if (state.isFilterVisible) {
      return (
        <div class="row mt-3">
          <div class="col-12 col-lg-2 offset-lg-8">
            <button
              class="btn btn-outline-dark w-100"
              onClick={this.resetFilters}
            >
              Reset Filters
            </button>
          </div>
          <div class="col-12 col-lg-2">
            <button class="btn btn-secondary w-100" onClick={this.applyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      );
    }
  }

  getFilterForm(state: DashPaymentApprovalState) {
    let form = state.filterForm;

    if (form && state.isFilterVisible) {
      return (
        <div class="row mt-3">
          {state.currentUser.role === ROLE_MANAGEMENT ||
          state.currentUser.role === ROLE_AUDITOR ? (
            <div class="col-12 col-lg-4 mt-1">
              <label class="dash-remittance__filter-row__filters__label">
                Supervisor
              </label>
              <AutocompleteSearch
                onResultSelected={this.onSupervisorSearchChange}
              />
            </div>
          ) : null}

          <div class="col-12 col-lg-4 mt-1">
            <label class="dash-remittance__filter-row__filters__label">
              Loan ID
            </label>
            <input
              className={cx("form-control")}
              type="text"
              value={form.fields["loanId"].value}
              onChange={linkform(this.form$, form, "loanId")}
              placeholder="Loan ID"
            />
          </div>

          <div class="col-12 col-lg-4 mt-1 mb-1">
            <label class="dash-remittance__filter-row__filters__label">
              Loan Payment ID
            </label>
            <input
              className={cx("form-control")}
              type="text"
              value={form.fields["loanPaymentId"].value}
              onChange={linkform(this.form$, form, "loanPaymentId")}
              placeholder="Loan Payment ID"
            />
          </div>

          <div class="col-12 col-lg-4 mt-1 mb-1">
            <label class="dash-remittance__filter-row__filters__label">
              Receiving Bank Account
            </label>

            <select
              className={cx("form-control")}
              placeholder="Bank Account"
              value={form.fields["receivingBankAccount"].value}
              onChange={linkform(this.form$, form, "receivingBankAccount")}
            >
              <option class="text-muted" value="" disabled={true} selected>
                Select Account
              </option>
              {state.allBankAccounts.map((acc) => {
                return (
                  <option value={acc.bankAccountName}>
                    {acc.bankAccountName}
                  </option>
                );
              })}
            </select>
          </div>

          <div class="col-12 col-lg-4 mt-1 mb-1">
            <label class="dash-remittance__filter-row__filters__label">
              Mininmum Amount
            </label>
            <input
              className={cx("form-control")}
              type="text"
              value={form.fields["minAmount"].value}
              onChange={linkform(this.form$, form, "minAmount")}
              placeholder="Mininmum Amount"
            />
          </div>

          <div class="col-12 col-lg-4 mt-1 mb-1">
            <label class="dash-remittance__filter-row__filters__label">
              Maximum Amount
            </label>
            <input
              className={cx("form-control")}
              type="text"
              value={form.fields["maxAmount"].value}
              onChange={linkform(this.form$, form, "maxAmount")}
              placeholder="Maximum Amount"
            />
          </div>

          <div class="col-12 col-lg-4 mt-1">
            <label class="dash-remittance__filter-row__filters__label">
              Start date
            </label>
            <DatePicker
              onDateSelected={this.startDateSelected}
              initialValue={form.fields["startDate"].value}
              isValid={form.fields["startDate"].isValid}
              isPristine={form.fields["startDate"].isPristine}
              reset={this.resetStartDates}
            ></DatePicker>
          </div>

          <div class="col-12 col-lg-4 mt-1">
            <label class="dash-remittance__filter-row__filters__label">
              End date
            </label>
            <DatePicker
              onDateSelected={this.endDateSelected}
              initialValue={form.fields["endDate"].value}
              isValid={form.fields["endDate"].isValid}
              isPristine={form.fields["endDate"].isPristine}
              reset={this.resetEndDates}
            ></DatePicker>
          </div>
        </div>
      );
    }
  }

  getPaymentActions(
    state: DashPaymentApprovalState,
    row: LoanPayment,
    index: number
  ) {
    const role = state.currentUser.role;

    if (role === ROLE_MANAGEMENT || role === ROLE_AUDITOR) {
      return (
        <div class="dropdown-menu" aria-labelledby={`actions${index}`}>
          <button
            class="dropdown-item"
            onClick={(e) => this.onModalOpened(this.MODAL_APPROVE_PAYMENT, row)}
          >
            Approve
          </button>
          <button
            class="dropdown-item"
            onClick={(e) => this.onModalOpened(this.MODAL_REJECT_PAYMENT, row)}
          >
            Reject
          </button>
        </div>
      );
    }
  }

  getPaginationBlock(state: DashPaymentApprovalState) {
    return (
      <div class="row">
        <div class="col-12">
          {state.pages ? (
            <div class="dash-remittance__pagination mb-3">
              <button
                class="btn dash-remittance__pagination__page arrow"
                disabled={state.currentPage === 1}
                onClick={() => this.onPageClicked(state.currentPage - 1)}
              >
                <i class="fas fa-chevron-left"></i>
              </button>
              {state.currentPage > 5 ? (
                <button
                  className={cx("btn dash-remittance__pagination__page", {
                    active: state.pages[0] === state.currentPage,
                  })}
                  onClick={() => this.onPageClicked(state.pages[0])}
                >
                  {state.pages[0]}
                </button>
              ) : null}
              {state.currentPage > 5 ? <span class="mx-2">...</span> : null}
              {state.pages
                ? state.pages.map((page) => {
                    if (
                      page > state.currentPage - 5 &&
                      page < state.currentPage + 5
                    ) {
                      return (
                        <button
                          className={cx(
                            "btn dash-remittance__pagination__page",
                            { active: page === state.currentPage }
                          )}
                          onClick={() => this.onPageClicked(page)}
                        >
                          {page}
                        </button>
                      );
                    }
                  })
                : null}
              {state.currentPage < state.pages.length - 5 ? (
                <span class="mx-2">...</span>
              ) : null}
              {state.currentPage < state.pages.length - 5 ? (
                <button
                  className={cx("btn dash-remittance__pagination__page", {
                    active:
                      state.pages[state.pages.length - 1] === state.currentPage,
                  })}
                  onClick={() =>
                    this.onPageClicked(state.pages[state.pages.length - 1])
                  }
                >
                  {state.pages[state.pages.length - 1]}
                </button>
              ) : null}
              <button
                class="btn dash-remittance__pagination__page arrow"
                disabled={
                  state.currentPage === state.pages[state.pages.length - 1]
                }
                onClick={() => this.onPageClicked(state.currentPage + 1)}
              >
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  getGridBlock = (state: DashPaymentApprovalState) => {
    return state.allPayments ? (
      <div class="dash-cashbon-loan___grid-container">
        <div class="row mt-3">
          <div class="col-12">
            <div class="data-grid">
              <div class="data-grid__row header">
                <div class="data-grid__col">Actions</div>
                <div class="data-grid__col">Loan ID</div>
                <div class="data-grid__col">Payment ID</div>
                <div class="data-grid__col">Supervisor</div>
                <div class="data-grid__col">Agent</div>
                <div class="data-grid__col">Status</div>
                <div class="data-grid__col">Amount Paid</div>
                <div class="data-grid__col">Bank</div>
                <div class="data-grid__col">Date Paid</div>
              </div>

              {state.allPayments.map((row, index) => {
                return (
                  <div class="data-grid__row">
                    <div class="data-grid__col">
                      <div class="dropright">
                        <button
                          class="btn btn-outline-primary dropdown-toggle"
                          type="button"
                          id={`actions${index}`}
                          data-toggle="dropdown"
                          aria-haspopup="true"
                          aria-expanded="false"
                        >
                          Select action
                        </button>
                        {this.getPaymentActions(state, row, index)}
                      </div>
                    </div>
                    <div class="data-grid__col">{row.loanId}</div>
                    <div class="data-grid__col">{row.loanPaymentId}</div>
                    <div class="data-grid__col">{row.supervisor}</div>
                    <div class="data-grid__col">
                      {row.agent.firstName} {row.agent.lastName}
                    </div>
                    <div class="data-grid__col text-capitalize">
                      <b>{row.status}</b>
                    </div>
                    <div class="data-grid__col">
                      <i>₱ {toDecimal(row.amount)}</i>
                    </div>
                    <div class="data-grid__col">{row.receivingBankAccount}</div>
                    <div class="data-grid__col">
                      {moment(row.dateOfTransaction).format("MMMM DD, YYYY")}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    ) : null;
  };

  getPortalModal = (state: DashPaymentApprovalState) => {
    let modalState = state.modalState as number;

    if (modalState < 0) {
      return null;
    } else if (modalState === this.MODAL_APPROVE_PAYMENT) {
      return (
        <Portal into="body">
          <div class="anglo-modal-container" id="anglo-modal-124">
            <ApproveLoanPaymentModal
              loanPayment={state.focusedPayment}
              currentUser={state.currentUser}
              close={this.onModalClose}
              isOutsideClickClose={false}
              containerId="anglo-modal-123"
            />
          </div>
        </Portal>
      );
    } else if (modalState === this.MODAL_REJECT_PAYMENT) {
      return (
        <Portal into="body">
          <div class="anglo-modal-container" id="anglo-modal-124">
            <RejectLoanPaymentModal
              loanPayment={state.focusedPayment}
              currentUser={state.currentUser}
              close={this.onModalClose}
              isOutsideClickClose={false}
              containerId="anglo-modal-123"
            />
          </div>
        </Portal>
      );
    }
  };

  render(props, state: DashPaymentApprovalState) {
    return (
      <div class="dash-cashbond-loans anglo__dashboard">
        {this.getPortalModal(state)}

        <div class="container-fluid">
          <div class="row">
            <div class="col-8 col-lg-4">
              <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                  <li class="breadcrumb-item">
                    <a href="/disposal">Payments Approval</a>
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {this.getActionsBlock(state)}
          {this.getFilterBlock(state)}
          {this.getGridBlock(state)}

          {this.getPaginationBlock(state)}
        </div>
      </div>
    );
  }
}
